import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';

import { makeStyles } from '@mui/styles';
import { ContentCopy as ContentCopyIcon, Visibility as VisibilityIcon } from '@mui/icons-material';

import StatusCell from '../../sharedModules/layout/StatusCell';
import ListPage from '../../sharedModules/listPage/ListPage';

const useStyles = makeStyles(() => ({
  queryPreview: {
    fontFamily: 'monospace',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
  },
  fullQuery: {
    fontFamily: 'monospace',
    fontSize: '13px',
    whiteSpace: 'pre-wrap',
    backgroundColor: '#f5f5f5',
    padding: '16px',
    borderRadius: '4px',
    overflowX: 'auto',
  },
}));

const subCategories = {
  backflows: ['BackflowJobItem UPDATE', 'BackflowLog Insert', 'BackflowJobItem Insert'],
  chillers: ['water_cooled_chiller', 'air_cooled_chiller'],
  customer_issues: ['customer_issue_update', 'customer_issue_insert'],
  home_services: ['InspectionReport Insert'],
  project_communications: [
    'project_communication_job_update',
    'project_communication_cal_update',
    'project_communication_update',
    'project_communication_cal_insert',
  ],
};

const subTitles = {
  backflows: 'Backflow Testing',
  chillers: 'Chiller Logs',
  customer_issues: 'Customer Issues',
  home_services: 'Home Services',
  project_communications: 'Project Communications',
};

interface ServmanSqlLogsPageProps {
  category?: string;
}

const ServmanSqlLogsPage: React.FC<ServmanSqlLogsPageProps> = ({ category: categoryProp }) => {
  const classes = useStyles();
  const location = useLocation();
  const [selectedQuery, setSelectedQuery] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const initialCategory = categoryProp || new URLSearchParams(location.search).get('category') || '';
  const [selectedCategory, setSelectedCategory] = useState<string>(initialCategory);

  const subCategoriesToUse = subCategories[selectedCategory as keyof typeof subCategories] || [];
  const categoryTitle = subTitles[selectedCategory as keyof typeof subTitles] || 'All';

  const handleViewQuery = (query: string) => {
    setSelectedQuery(query);
    setOpenDialog(true);
  };

  const handleCopyQuery = (query: string) => {
    navigator.clipboard.writeText(query);
    setCopySuccess(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedQuery(null);
  };

  const columns = [
    {
      name: 'ID',
      selector: 'id',
      width: '80px',
    },
    {
      name: 'Record ID',
      selector: 'loggable_id',
      width: '80px',
    },
    {
      name: 'Status',
      selector: 'status',
      sortable: true,
      width: '100px',
      cell: (row: any) => <StatusCell status={row.status} />,
    },
    {
      name: 'Category',
      selector: 'category',
      sortable: true,
      width: '180px',
      cell: (row: any) => <Chip label={row.category} size="small" />,
    },
    {
      name: 'Order No',
      selector: 'order_no',
      sortable: true,
      width: '120px',
    },
    {
      name: 'SQL Query Preview',
      selector: 'query',
      sortable: false,
      grow: 2,
      cell: (row: any) => <Box className={classes.queryPreview}>{row.query}</Box>,
    },
    {
      name: 'Location',
      selector: 'db_location',
      sortable: true,
      grow: 0.8,
    },
    {
      name: 'Updated At',
      selector: 'updated_at_with_time',
      sortable: true,
      grow: 0.8,
    },
    {
      name: 'Actions',
      width: '120px',
      cell: (row: any) => (
        <Box display="flex" gap={1}>
          <IconButton size="small" color="primary" onClick={() => handleViewQuery(row.query)} title="View Full Query">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="secondary" onClick={() => handleCopyQuery(row.query)} title="Copy Query">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const defaultFilters = { category_in: subCategoriesToUse };

  const filterDefinitions = [
    {
      label: 'Status',
      type: 'select',
      param: 'status_eq',
      choices: [
        { label: 'Success', value: 'success' },
        { label: 'Error', value: 'error' },
        { label: 'resolved', value: 'resolved' },
      ],
    },
    {
      label: 'Location',
      type: 'select',
      param: 'db_location_eq',
      choices: [
        { label: 'Pittsburgh', value: 'pittsburgh' },
        { label: 'Tampa', value: 'tampa' },
      ],
    },
    {
      label: 'Sub Category',
      type: 'select',
      param: 'category_in',
      choices: subCategoriesToUse.map((cat) => ({ label: cat, value: cat })),
    },
  ];

  const categorySelect = (
    <FormControl size="small" style={{ minWidth: 200 }}>
      <InputLabel>Category</InputLabel>
      <Select value={selectedCategory} label="Category" onChange={(e) => setSelectedCategory(e.target.value)}>
        <MenuItem value="">All</MenuItem>
        {Object.keys(subCategories).map((key) => (
          <MenuItem key={key} value={key}>
            {subTitles[key as keyof typeof subTitles] || key}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const fullQueryDialog = (
    <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Full SQL Query
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box className={classes.fullQuery}>{selectedQuery}</Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => selectedQuery && handleCopyQuery(selectedQuery)} variant="outlined">
          Copy to Clipboard
        </Button>
        <Button onClick={handleCloseDialog} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <ListPage
      key={selectedCategory || 'all'}
      title="Servman SQL Logs"
      titleIcon={<StorageIcon />}
      subtitle={categoryTitle}
      controller="/servman_sql_logs"
      columns={columns}
      defaultFilters={defaultFilters}
      clearedFilterSetting={defaultFilters}
      filterDefinitions={filterDefinitions}
      includeFilterBar
      beforeComponent={!categoryProp ? categorySelect : undefined}
      defaultSort="created_at desc"
      perPage={50}
      afterComponent={
        <>
          {fullQueryDialog}
          <Snackbar
            open={copySuccess}
            autoHideDuration={2000}
            onClose={() => setCopySuccess(false)}
            message="Query copied to clipboard!"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          />
        </>
      }
    />
  );
};

export default ServmanSqlLogsPage;
